<script lang="ts" setup>
import Container from '~/components/Container.vue';
import { ref } from 'vue';
import { A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Swiper as SwiperClass, SwiperModule } from 'swiper/types';
import { Icon } from '@iconify/vue';
import PromoCardsItem, { Item } from './PromoCards/Item.vue';

interface Props {
  anchor?: string;
  title?: string;
  description?: string;
  items: Item[];
}

defineProps<Props>();

const modules: SwiperModule[] = [A11y];
const swiperUse = ref<SwiperClass | null>(null);

function onSwiper(swiper: SwiperClass) {
  swiperUse.value = swiper;
}

const swiperOptions = {
  init: false,
  wrapperTag: 'div',
  modules,
  slidesPerView: 'auto',
  keyboard: {
    enabled: true,
    onlyInViewport: false,
  },
};
</script>

<template>
  <section v-if="items && items.length > 0" :id="anchor" class="promo-cards">
    <Container class="promo-cards__container">
      <div v-if="title || description" class="promo-cards__heading">
        <h2 v-if="title" class="promo-cards__title">{{ title }}</h2>
        <p v-if="description" class="promo-cards__description">{{ description }}</p>
      </div>
      <div class="promo-cards__slider">
        <Swiper v-bind="swiperOptions" @swiper="onSwiper">
          <SwiperSlide v-for="(item, index) in items" :key="`promo-cards-item-${index}`">
            <PromoCardsItem :item="item" />
          </SwiperSlide>
        </Swiper>
        <button
          class="swiper-button-prev"
          :class="{
            'swiper-button-disabled': swiperUse?.isBeginning,
            'swiper-button-lock': swiperUse?.isLocked,
          }"
          @click="swiperUse?.slidePrev()"
        >
          <span class="sr-only">Prev Slide</span>
          <Icon icon="bi:arrow-left" />
        </button>
        <button
          class="swiper-button-next"
          :class="{
            'swiper-button-disabled': swiperUse?.isEnd,
            'swiper-button-lock': swiperUse?.isLocked,
          }"
          @click="swiperUse?.slideNext()"
        >
          <span class="sr-only">Next Slide</span>
          <Icon icon="bi:arrow-right" />
        </button>
      </div>
    </Container>
  </section>
</template>

<style lang="scss" scoped>
.promo-cards {
  @apply my-10 overflow-hidden;
  @apply md:my-16;
  @apply xl:my-24;

  &__heading {
    @apply flex flex-col items-center text-center mb-6 gap-6;
    @apply xl:mb-12 xl:gap-10;
  }

  &__title {
    @apply font-semibold text-[24px] leading-[1.1];
    @apply xl:text-[38px];
  }

  &__description {
    @apply font-medium leading-[1.3] text-[16px] text-base-color/80;
    @apply xl:text-[20px] xl:max-w-[60%] xl:mx-auto;
  }

  &__slider {
    @apply flex flex-wrap justify-center;
  }

  .swiper {
    @apply overflow-visible w-[calc(100%+1.5rem)];

    &-button-prev,
    &-button-next {
      @apply static text-dark after:hidden translate-y-0 mt-10 mx-1 h-auto w-auto p-2 [&>svg]:w-10 duration-300 hover:text-accent;
    }
  }

  :deep(.swiper-slide) {
    @apply w-full px-3;
    @apply md:w-1/2;
    @apply xl:w-1/3;
  }
}
</style>
